import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class ThemeService {
  private isDarkModeSubject = new BehaviorSubject<boolean>(false);
  public isDarkMode$ = this.isDarkModeSubject.asObservable();

  constructor() {
    // Apply theme immediately to avoid flash of default theme
    this.initializeTheme();
  }

  private initializeTheme(): void {
    // Check for saved theme preference or default to system preference
    const savedTheme = localStorage.getItem('theme');
    const prefersDark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
    
    const isDark = savedTheme === 'dark' || (!savedTheme && prefersDark);
    
    // Ensure theme classes are properly applied
    this.applyThemeClasses(isDark);
    
    // Remove any inline styles that were applied for flash prevention
    document.documentElement.style.removeProperty('--background-color');
    document.documentElement.style.removeProperty('--text-color');
    
    this.isDarkModeSubject.next(isDark);
  }

  private applyThemeClasses(isDark: boolean): void {
    const themeClass = isDark ? 'dark-theme' : 'light-theme';
    const otherClass = isDark ? 'light-theme' : 'dark-theme';
    
    // Apply to html element
    document.documentElement.classList.remove(otherClass);
    document.documentElement.classList.add(themeClass);
    
    // Apply to body element
    document.body.classList.remove(otherClass);
    document.body.classList.add(themeClass);
  }

  setDarkMode(isDark: boolean): void {
    this.isDarkModeSubject.next(isDark);
    localStorage.setItem('theme', isDark ? 'dark' : 'light');
    
    // Update CSS classes
    this.applyThemeClasses(isDark);
  }

  toggleDarkMode(): void {
    this.setDarkMode(!this.isDarkModeSubject.value);
  }

  isDarkMode(): boolean {
    return this.isDarkModeSubject.value;
  }
}
